import { useCallback, useEffect, useMemo, useState } from 'react'
import useTranslator from '~/lib/useTranslator'
import convertToCurrency from '~/utils/convertToCurrency'

type ProgressBarProps = {
  current: number
  goal: number
  currency?: string
  locale?: string
  isCampaignActive?: boolean
}

export function ProgressBar({
  current,
  goal,
  currency = 'USD',
  locale,
  isCampaignActive = true,
}: ProgressBarProps) {
  const Translator = useTranslator()

  const hasSale = current !== 0
  const hasExceededGoal = current > goal

  const rawProgressRatio = goal > 0 ? current / goal : 0
  const progressRatio = hasExceededGoal ? Math.min(rawProgressRatio, 0.98) : rawProgressRatio
  const MIN_PROGRESS_PERCENT = 2

  const goalPosition = useMemo(() => {
    if (current > goal) {
      return Math.max(30, (goal / current) * 100)
    }
    return 100
  }, [current, goal])
  const isMinWidth = current / goal < 0.2

  const calculateAnimationDuration = useCallback(() => {
    const distance = progressRatio
    const duration = distance * 1000
    return Math.max(400, Math.min(duration, 2000))
  }, [progressRatio])

  const [animatedWidth, setAnimatedWidth] = useState(0)
  const [animationStarted, setAnimationStarted] = useState(false)

  useEffect(() => {
    const targetPercent = Math.min(progressRatio * 100, 100)
    const displayPercent = hasSale ? Math.max(targetPercent, MIN_PROGRESS_PERCENT) : targetPercent

    setAnimationStarted(true)
    setAnimatedWidth(displayPercent)

    requestAnimationFrame(() => {
      setAnimatedWidth(displayPercent)
    })
  }, [progressRatio, calculateAnimationDuration, hasSale])

  return (
    <section aria-labelledby="progress-title" className="gap-40">
      <div className="flex flex-row justify-between">
        <p className="text-m mt-1 text-neutral-900 font-semibold">
          {Translator.singular('My goal')}
        </p>
      </div>

      <div className="relative w-full">
        <progress
          className="sr-only"
          value={current}
          max={goal}
          aria-label={`${current} sold of ${goal} goal`}
        />
        <div className="flex flex-row justify-center items-end gap-2">
          <div
            className="w-full bg-gray-200 rounded-full h-4 flex items-center justify-between"
            role="presentation"
            aria-hidden="true"
          >
            <div
              className={`bg-primary-color bg-linear-to-l from-primary-color to-blue-500/30 h-full ${
                animationStarted ? 'transition-all ease-in-out' : ''
              } ${animatedWidth >= 100 ? 'rounded-full' : 'rounded-l-full'}`}
              style={{
                width: `${animatedWidth}%`,
                transitionDuration: animationStarted ? `${calculateAnimationDuration()}ms` : '0ms',
              }}
            ></div>
            <div
              className="absolute top-1 bottom-7 w-0.5 bg-white"
              style={{
                left: `${goalPosition}%`,
                transform: 'translateX(-50%)',
                zIndex: 10,
                display: goalPosition > 0 ? 'block' : 'none',
              }}
              aria-hidden="true"
            />
            <div
              className="absolute -top-6 text-m text-neutral-900 font-normal"
              style={{
                left: `${goalPosition}% `,
                transform: hasExceededGoal ? 'translateX(-50%)' : 'translateX(-100%)',
                width: 'max-content',
              }}
            >
              <p className="font-semibold">{convertToCurrency(locale, currency, goal * 100, 0)}</p>
            </div>
          </div>
          <p className="text-xl -mb-1">🏆</p>
        </div>
        {hasSale && (
          <div className="relative h-8">
            <div
              className={`absolute text-sm font-semibold flex ${isMinWidth ? 'justify-start' : 'justify-end'} pt-2`}
              style={{
                width: `${animatedWidth}%`,
                opacity: animationStarted ? 1 : 0,
                transition: 'opacity 1s',
                transitionDelay: calculateAnimationDuration() - 250 + 'ms',
              }}
            >
              <p className="whitespace-nowrap">
                {convertToCurrency(locale, currency, current * 100, 0)} sold
              </p>
            </div>
          </div>
        )}
      </div>
      {hasExceededGoal && (
        <div
          className={`bg-green-50 px-4 py-1 rounded-3xl mt-2 mb-4 mx-auto w-fit`}
          style={{
            opacity: animationStarted ? 1 : 0,
            transition: 'opacity 0.5s, transform 0.5s',
            transitionDelay: calculateAnimationDuration() + 'ms',
            transform: animationStarted ? 'scale(1)' : 'scale(0.9)',
          }}
        >
          <p>
            {isCampaignActive
              ? Translator.singular("Goal smashed! 🚀 Let's keep going!")
              : Translator.singular('Goal smashed! 🚀')}
          </p>
        </div>
      )}
    </section>
  )
}
